import {
  Box,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Switch,
  Typography,
  type SelectChangeEvent,
} from '@mui/material';
import { useMemo } from 'react';
import { useTranslations } from '@/i18n/use-translations';
import type {
  MediaControlActionKey,
  MediaControlBindings,
  MediaControlBindingGesture,
  ResolvedMediaControlSettings,
} from '@/types/playout/media-control-settings.types';
import { MEDIA_CONTROL_BINDING_GESTURES } from '@/types/playout/media-control-settings.types';

const ROWS: Array<{ key: MediaControlActionKey | 'slide'; labelKey: string; fixed?: string }> = [
  { key: 'slide', labelKey: 'playout.mediaControl.rows.slide', fixed: 'single_click' },
  { key: 'media', labelKey: 'playout.mediaControl.rows.media' },
  { key: 'segment', labelKey: 'playout.mediaControl.rows.segment' },
  { key: 'flow', labelKey: 'playout.mediaControl.rows.flow' },
];

export interface MediaControlSettingsFormProps {
  settings: ResolvedMediaControlSettings;
  disabled?: boolean;
  showAllowEventOverride?: boolean;
  showAllowFlowOverride?: boolean;
  onBindingsChange: (bindings: MediaControlBindings) => void;
  onAllowEventOverrideChange?: (value: boolean) => void;
  onAllowFlowOverrideChange?: (value: boolean) => void;
}

function gestureLabel(t: ReturnType<typeof useTranslations>, gesture: MediaControlBindingGesture | 'single_click') {
  return t(`playout.mediaControl.gestures.${gesture}` as never, {
    defaultValue:
      gesture === 'single_click'
        ? '1x click'
        : gesture === 'double_click'
          ? '2x click'
          : gesture === 'triple_click'
            ? '3x click'
            : gesture === 'quadruple_click'
              ? '4x click'
              : gesture === 'none'
                ? 'None'
                : 'Long press',
  });
}

export function MediaControlSettingsForm({
  settings,
  disabled = false,
  showAllowEventOverride = false,
  showAllowFlowOverride = false,
  onBindingsChange,
  onAllowEventOverrideChange,
  onAllowFlowOverrideChange,
}: MediaControlSettingsFormProps) {
  const t = useTranslations();
  const bindings = settings.bindings;

  const usedGestures = useMemo(() => {
    return new Set<MediaControlBindingGesture>(
      [bindings.media, bindings.segment, bindings.flow].filter((gesture) => gesture !== 'none'),
    );
  }, [bindings]);

  const handleGestureChange = (row: MediaControlActionKey, gesture: MediaControlBindingGesture) => {
    onBindingsChange({ ...bindings, [row]: gesture });
  };

  const renderGestureSelect = (row: MediaControlActionKey) => (
    <FormControl size="small" fullWidth disabled={disabled}>
      <InputLabel id={`media-control-${row}`}>
        {t('playout.mediaControl.gestureLabel', { defaultValue: 'Gesture' })}
      </InputLabel>
      <Select
        labelId={`media-control-${row}`}
        label={t('playout.mediaControl.gestureLabel', { defaultValue: 'Gesture' })}
        value={bindings[row]}
        onChange={(event: SelectChangeEvent) =>
          handleGestureChange(row, event.target.value as MediaControlBindingGesture)
        }
      >
        {MEDIA_CONTROL_BINDING_GESTURES.map((gesture) => {
          const takenElsewhere = gesture !== 'none' && usedGestures.has(gesture) && bindings[row] !== gesture;
          return (
            <MenuItem key={gesture} value={gesture} disabled={takenElsewhere}>
              {gestureLabel(t, gesture)}
            </MenuItem>
          );
        })}
      </Select>
    </FormControl>
  );

  return (
    <Stack spacing={2}>
      <Typography variant="body2" color="text.secondary">
        {t('playout.mediaControl.description', {
          defaultValue:
            'Configure your presentation clicker on stage and room players. The forward and back buttons always move one slide on a single click. Assign double-click, triple-click, and long-press to the actions below, or choose None to disable an action.',
        })}
      </Typography>

      {ROWS.map((row) => (
        <Box
          key={row.key}
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', sm: '1fr 220px' },
            gap: 1.5,
            alignItems: 'center',
          }}
        >
          <Typography variant="body2">
            {t(row.labelKey as never, {
              defaultValue:
                row.key === 'slide'
                  ? 'Next / previous slide'
                  : row.key === 'media'
                    ? 'Next / previous media'
                    : row.key === 'segment'
                      ? 'Next / previous segment'
                      : 'Start / end flow',
            })}
          </Typography>
          {row.fixed ? (
            <Typography variant="body2" color="text.secondary">
              {gestureLabel(t, row.fixed as 'single_click')}
            </Typography>
          ) : row.key === 'media' || row.key === 'segment' || row.key === 'flow' ? (
            renderGestureSelect(row.key)
          ) : null}
        </Box>
      ))}

      {showAllowEventOverride && (
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2 }}>
          <Typography variant="body2">
            {t('playout.mediaControl.allowEventOverride', {
              defaultValue: 'Allow events to override these settings',
            })}
          </Typography>
          <Switch
            checked={settings.allowEventOverride}
            disabled={disabled}
            onChange={(_, checked) => onAllowEventOverrideChange?.(checked)}
          />
        </Box>
      )}

      {showAllowFlowOverride && (
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2 }}>
          <Typography variant="body2">
            {t('playout.mediaControl.allowFlowOverride', {
              defaultValue: 'Allow flows to override these settings',
            })}
          </Typography>
          <Switch
            checked={settings.allowFlowOverride}
            disabled={disabled}
            onChange={(_, checked) => onAllowFlowOverrideChange?.(checked)}
          />
        </Box>
      )}
    </Stack>
  );
}
